import { BrowserRouter as Router, Route, Routes, Navigate, Outlet, useNavigate, useParams, useLocation } from "react-router-dom";
import { lazy, Suspense, useContext, useEffect, useLayoutEffect, useRef } from "react";
import { Toaster } from "sonner";

import "./App.css";

// providers / contexts
import { AuthProvider, AuthContext } from "../contexts/AuthContext";
import { CartProvider } from "../contexts/CartContext";
import { OwnerAuthProvider, OwnerAuthContext } from "../contexts/OwnerAuthContext";

// app helpers (moved into src/app)
import AddToHomeScreenBanner from "./AddToHomeScreenBanner";
import WelcomeOfferBanner from "./WelcomeOfferBanner";
import ScrollToTop from "./ScrollToTop";
import SessionExpiredBridge from "./SessionExpiredBridge";
import NotificationPermissionBanner from "./NotificationPermissionBanner";
import PageSkeleton from "../components/ui/PageSkeleton";

// api (still in src/)
import api, { setCsrfToken } from "../api/client";

// route guards (now in src/app/routes/guards)
import UserProtectedRoute from "./routes/guards/UserProtectedRoute";
import OwnerProtectedRoute from "./routes/guards/OwnerProtectedRoute";
// import ProtectedRoute from "./routes/guards/ProtectedRoute"; // only if you actually use it

// layout
import CookieBanner from "../components/layout/CookieBanner";
import { SuperAdminAuthProvider, SuperAdminAuthContext } from "../contexts/SuperAdminAuthContext";
import { bootstrapSessionForPath } from "../utils/authRealm";
import { SiteSettingsProvider } from "../contexts/SiteSettingsContext";
import ErrorBoundary from "../components/common/ErrorBoundary";
import UserLayout from "../features/user/UserLayout";

import SuperAdminProtectedRoute from "./routes/guards/SuperAdminProtectedRoute";

const page = (loader) => lazy(loader);

const CookiesPolicy = page(() => import("../policies/CookiesPolicy"));
const PrivacyPolicy = page(() => import("../policies/PrivacyPolicy"));
const TermsConditions = page(() => import("../policies/TermsConditions"));
const LegalNotice = page(() => import("../policies/LegalNotice"));
const TermsOfSale = page(() => import("../policies/TermsOfSale"));
const SupportPage = page(() => import("../features/support/SupportPage"));
const UserSearch = page(() => import("../pages/UserSearch"));
const BoulangerieItems = page(() => import("../pages/BoulangerieItems"));
const UserLogin = page(() => import("../features/auth/user/UserLogin"));
const UserSignup = page(() => import("../features/auth/user/UserSignUp"));
const ActivateAccount = page(() => import("../features/auth/user/ActivateAccount"));
const UserForgotPassword = page(() => import("../features/auth/user/UserForgotPassword"));
const UserResetPassword = page(() => import("../features/auth/user/UserResetPassword"));
const OwnerLoginForm = page(() => import("../features/auth/owner/OwnerLoginForm"));
const EmployeeLoginForm = page(() => import("../features/auth/owner/EmployeeLoginForm"));
const OwnerSignUpForm = page(() => import("../features/auth/owner/OwnerSignUpForm"));
const OwnerActivateAccount = page(() => import("../features/auth/owner/OwnerActivateAccount"));
const OwnerForgotPassword = page(() => import("../features/auth/owner/OwnerForgotPassword"));
const OwnerResetPassword = page(() => import("../features/auth/owner/OwnerResetPassword"));
const RestaurantPaymentSetup = page(() => import("../features/auth/owner/RestaurantPaymentSetup"));
const CartPage = page(() => import("../features/cart/CartPage"));
const CounterOrderSuccessPage = page(() => import("../features/cart/CounterOrderSuccessPage"));
const OrderPage = page(() => import("../features/orders/shared/OrderPage"));
const MessagesPage = page(() => import("../features/messages/MessagesPage"));
const UserOrdersPage = page(() => import("../features/orders/user/UserOrdersPage"));
const UserDiningSessionOrderPage = page(() => import("../features/orders/user/UserDiningSessionOrderPage"));
const UserSettingsPage = page(() => import("../features/menu/user/UserSettingsPage"));
const NotificationInboxPage = page(() => import("../features/notifications/NotificationInboxPage"));
const EditSavedPlacePage = page(() => import("../features/menu/user/EditSavedPlacePage"));
const TokenDetails = page(() => import("../features/orders/user/TokenDetails"));
const CheckoutPayment = page(() => import("../features/payments/CheckoutPayment"));
const SuccessPage = page(() => import("../features/payments/SuccessPage"));
const PublicPaymentLinkPage = page(() => import("../features/payments/PublicPaymentLinkPage"));
const PayLinkResultPage = page(() => import("../features/payments/PayLinkResultPage"));
const PrintOrderPage = page(() => import("../features/print/PrintOrderPage"));
const SuperAdmin = page(() => import("../features/admin/SuperAdmin"));
const SuperAdminLogin = page(() => import("../features/admin/SuperAdminLogin"));
const SuperAdminSignup = page(() => import("../features/admin/SuperAdminSignup"));
const SuperAdminForgotPassword = page(() => import("../features/admin/SuperAdminForgotPassword"));
const SuperAdminResetPassword = page(() => import("../features/admin/SuperAdminResetPassword"));
const SuperAdminSetPassword = page(() => import("../features/admin/SuperAdminSetPassword"));
const SuperAdminLogout = page(() => import("../features/admin/SuperAdminLogout"));

const PickupWindowSelector = page(() => import("../components/common/PickupWindowSelector"));
const NewSelectionPage = page(() => import("../features/menu/user/NewSelectionPage"));
const FavoritesPage = page(() => import("../features/menu/user/FavoritesPage"));
const PlannedMealCheckoutPage = page(() => import("../features/menu/user/PlannedMealCheckoutPage"));
const RestaurantMenuPage = page(() => import("../features/menu/user/RestaurantMenuPage"));
const RedeemPointsPage = page(() => import("../features/menu/user/RedeemPointsPage"));
const UserPublicProfilePage = page(() => import("../features/menu/user/UserPublicProfilePage"));
const UserProfileStatsPage = page(() => import("../features/menu/user/UserProfileStatsPage"));
const TastesFeedPage = page(() => import("../features/menu/user/TastesFeedPage"));
const ItemFeedPage = page(() => import("../features/menu/user/ItemFeedPage"));
const TastePostPage = page(() => import("../features/menu/user/TastePostPage"));
const CombinationTriedPage = page(() => import("../features/menu/user/CombinationTriedPage"));
const ShareFromOrderPage = page(() => import("../features/menu/user/ShareFromOrderPage"));
const ShareOrderItemPage = page(() => import("../features/menu/user/ShareOrderItemPage"));
const RestaurantProfilePage = page(() => import("../features/menu/user/RestaurantProfilePage"));
const DineInReservePage = page(() => import("../features/menu/user/DineInReservePage"));
const UserDineInReservationsPage = page(() => import("../features/menu/user/UserDineInReservationsPage"));
const DiningTableSessionPage = page(() => import("../features/menu/user/DiningTableSessionPage"));
const DiningPlatformReservePage = page(() => import("../features/menu/user/DiningPlatformReservePage"));
const OwnerMenuBuilder = page(() => import("../features/menu/owner/Restaurant/menu-system/OwnerMenuBuilder"));
const AddItems = page(() => import("../features/menu/owner/Restaurant/AddItems"));
const AddCategoryAndItems = page(() => import("../features/menu/owner/Restaurant/AddCategoryAndItems"));
const Dashboard = page(() => import("../features/menu/owner/Restaurant/Dashboard"));
const MenuPage = page(() => import("../features/menu/owner/Restaurant/MenuPage"));
const DashboardLayout = page(() => import("../features/menu/owner/Restaurant/DashboardLayout"));
const OwnerOrders = page(() => import("../features/menu/owner/Restaurant/OwnerOrders"));
const OwnerPhoneOrderPage = page(() => import("../features/menu/owner/Restaurant/OwnerPhoneOrderPage"));
const OwnerOrdersMapPage = page(() => import("../features/menu/owner/Restaurant/OwnerOrdersMapPage"));
const InventoryPage = page(() => import("../features/inventory/InventoryPage"));
const OwnerTimingsHubLayout = page(() => import("../features/menu/owner/Restaurant/OwnerTimingsHubLayout"));
const OwnerTimingsHoursPage = page(() => import("../features/menu/owner/Restaurant/OwnerTimingsHoursPage"));
const OwnerTimingsModesPage = page(() => import("../features/menu/owner/Restaurant/OwnerTimingsModesPage"));
const OwnerDineInTablesPage = page(() => import("../features/menu/owner/Restaurant/OwnerDineInTablesPage"));
const OwnerDiningConsolePage = page(() => import("../features/menu/owner/Restaurant/OwnerDiningConsolePage"));
const OwnerSettingsPage = page(() => import("../features/menu/owner/Restaurant/OwnerSettingsPage"));
const OwnerDevicesPage = page(() => import("../features/menu/owner/Restaurant/OwnerDevicesPage"));
const OwnerOrderRulesPage = page(() => import("../features/menu/owner/Restaurant/OwnerOrderRulesPage"));
const OwnerRestaurantPage = page(() => import("../features/menu/owner/Restaurant/OwnerRestaurantPage"));
const OwnerStoriesPage = page(() => import("../features/menu/owner/Restaurant/OwnerStoriesPage"));
const OwnerFeedPage = page(() => import("../features/menu/owner/Restaurant/OwnerFeedPage"));
const StudentDiscountPolicyPage = page(() => import("../features/menu/owner/discount/StudentDiscountPolicyPage"));
const OwnerLoyaltyPage = page(() => import("../features/menu/owner/loyalty/OwnerLoyaltyPage"));
const LoyalCustomersPage = page(() => import("../features/menu/owner/customers/LoyalCustomersPage"));
const VerifyStudentPage = page(() => import("../features/menu/owner/students/VerifyStudentPage"));
const OwnerStudentsHubLayout = page(() => import("../features/menu/owner/students/OwnerStudentsHubLayout"));
const EmployeesPage = page(() => import("../features/menu/owner/Restaurant/EmployeesPage"));
const EmployeeSetPasswordPage = page(() => import("../features/menu/owner/Restaurant/EmployeeSetPasswordPage"));
const OwnerOffersPage = page(() => import("../features/menu/owner/offers/OwnerOffersPage"));

const RedirectRestaurantGouts = () => {
  const { restaurantId } = useParams();
  return <Navigate to={`/restaurant/${restaurantId}`} replace />;
};

const RedirectRestaurantOffers = () => {
  const { restaurantId } = useParams();
  return <Navigate to={`/restaurant/${restaurantId}?section=offers`} replace />;
};

/** Renders child routes at `/` (RR7: index under `path="/"` matches reliably; a flat `path="/"` route did not). */
const RootOutlet = () => <Outlet />;

/** Loads the one session that matches the URL: customer, restaurant, or super admin. */
const RoleSessionBootstrap = () => {
  const { pathname } = useLocation();
  const { userSessionChecked, refreshAuth } = useContext(AuthContext);
  const { ownerSessionChecked, refreshOwnerAuth } = useContext(OwnerAuthContext);
  const { adminSessionChecked, refreshAdminAuth } = useContext(SuperAdminAuthContext);
  const startedRef = useRef("");

  useEffect(() => {
    const session = bootstrapSessionForPath(pathname, {
      userSessionChecked,
      ownerSessionChecked,
      adminSessionChecked,
    });
    if (!session || startedRef.current === session) return;
    startedRef.current = session;
    if (session === "owner") refreshOwnerAuth();
    else if (session === "admin") refreshAdminAuth();
    else refreshAuth();
  }, [
    pathname,
    userSessionChecked,
    ownerSessionChecked,
    adminSessionChecked,
    refreshAuth,
    refreshOwnerAuth,
    refreshAdminAuth,
  ]);

  return null;
};

/** Opens the screen from a notification tap without reloading the app. */
const NotificationOpenBridge = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const go = (url) => {
      const next = String(url || "").trim();
      if (!next.startsWith("/")) return;
      const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      if (next !== current) navigate(next);
    };

    const pending = window.__pendingNotificationPath;
    if (pending) {
      window.__pendingNotificationPath = "";
      go(pending);
    }

    const onOpen = (event) => {
      window.__pendingNotificationPath = "";
      go(event.detail?.url);
    };
    window.addEventListener("notification-open", onOpen);
    return () => window.removeEventListener("notification-open", onOpen);
  }, [navigate]);

  return null;
};

/**
 * Home → `/explore`. RR7 client `navigate()` from the `/` index segment was still flaky in Chromium/CI
 * (URL stayed `/`). Full-document replace is deterministic for smoke tests and avoids Strict Mode
 * cancelling a fallback timeout on unmount when navigation appears to succeed.
 */
const ExploreHomeRedirect = () => {
  useLayoutEffect(() => {
    try {
      const p = window.location.pathname || "";
      if (p === "/" || p === "") {
        window.location.replace(`${window.location.origin}/explore`);
      }
    } catch {
      window.location.assign("/explore");
    }
  }, []);

  return null;
};

const App = () => {
  useEffect(() => {
    let cancelled = false;
    let unsubscribe;
    const start = () => {
      import("./FCMTokenHandler").then(({ setupOnMessageListener }) => {
        if (cancelled) return;
        unsubscribe = setupOnMessageListener();
      }).catch(() => {});
    };
    const idleId = window.requestIdleCallback
      ? window.requestIdleCallback(start, { timeout: 4000 })
      : window.setTimeout(start, 1200);
    return () => {
      cancelled = true;
      if (window.cancelIdleCallback && window.requestIdleCallback) window.cancelIdleCallback(idleId);
      else window.clearTimeout(idleId);
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, []);

  // Prime CSRF after the first screen's own requests. Login forms call ensureCsrfToken
  // themselves, and the response interceptor retries once on EBADCSRFTOKEN.
  useEffect(() => {
    const fetchCsrf = async () => {
      try {
        const res = await api.get("/api/csrf-token", { timeout: 15000 });
        setCsrfToken(res.data?.csrfToken);
      } catch {
        // Silent — interceptor recovers on the first 403 EBADCSRFTOKEN.
      }
    };
    const idleId = window.requestIdleCallback
      ? window.requestIdleCallback(() => fetchCsrf(), { timeout: 2500 })
      : window.setTimeout(fetchCsrf, 800);
    const interval = setInterval(fetchCsrf, 50 * 60 * 1000);
    return () => {
      if (window.cancelIdleCallback && window.requestIdleCallback) window.cancelIdleCallback(idleId);
      else window.clearTimeout(idleId);
      clearInterval(interval);
    };
  }, []);

  return (
    <ErrorBoundary>
    <SiteSettingsProvider>
    <AuthProvider>
      <OwnerAuthProvider>
        <SuperAdminAuthProvider>
          <CartProvider>
              {/* No global <Elements>: Connect checkout uses loadStripe(pk,{stripeAccount}) per page.
                  Nesting platform Elements caused useStripe() to resolve wrong instance → Invalid API Key on confirm. */}
              <Router>
                <RoleSessionBootstrap />
                <NotificationOpenBridge />
                <ScrollToTop />
                <SessionExpiredBridge />
                <div className="flex min-h-screen flex-col">
                  <AddToHomeScreenBanner />
                  <WelcomeOfferBanner />
                  <NotificationPermissionBanner />
                  <CookieBanner />

                  <div className="flex min-h-0 flex-1 flex-col">
                    <Suspense fallback={<div className="p-4 sm:p-6"><PageSkeleton /></div>}>
                    <Routes>
                  {/* Customer: home → browse; explicit /login for sign-in */}
                  <Route path="/" element={<RootOutlet />}>
                    <Route index element={<ExploreHomeRedirect />} />
                  </Route>
                  <Route path="/login" element={<UserLogin />} />
                  <Route path="/signup" element={<UserSignup />} />
                  <Route path="/forgot-password" element={<UserForgotPassword />} />
                  <Route path="/reset-password/:token" element={<UserResetPassword />} />
                  <Route path="/activate/:token" element={<ActivateAccount />} />

                  {/* Owner auth */}
                  <Route path="/restaurant/signup" element={<OwnerSignUpForm />} />
                  <Route path="/restaurant/login" element={<OwnerLoginForm />} />
                  <Route path="/restaurant/employee-login" element={<EmployeeLoginForm />} />
                  <Route path="/restaurant/forgot-password" element={<OwnerForgotPassword />} />
                  <Route path="/restaurant/reset-password/:token" element={<OwnerResetPassword />} />
                  <Route path="/restaurant/activate/:token" element={<OwnerActivateAccount />} />

                  {/* Super Admin auth */}
                  <Route path="/admin/login" element={<SuperAdminLogin />} />
                  <Route path="/admin/signup" element={<SuperAdminSignup />} />
                  <Route path="/admin/forgot-password" element={<SuperAdminForgotPassword />} />
                  <Route path="/admin/reset-password/:token" element={<SuperAdminResetPassword />} />
                  <Route path="/admin/logout" element={<SuperAdminLogout />} />

                  {/* Policies & utilities */}
                  <Route path="/boulangerie/add-items" element={<AddItems />} />
                  <Route path="/boulangerie/add-category" element={<AddCategoryAndItems />} />
                  <Route path="/p/:token" element={<PublicPaymentLinkPage />} />
                  <Route path="/pay/success" element={<PayLinkResultPage kind="success" />} />
                  <Route path="/pay/cancel" element={<PayLinkResultPage kind="cancel" />} />
                  <Route path="/cookies" element={<CookiesPolicy />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/terms" element={<TermsConditions />} />
                  <Route path="/legal" element={<LegalNotice />} />
                  <Route path="/terms-of-sale" element={<TermsOfSale />} />
                  <Route path="/support" element={<SupportPage />} />

                  {/* Customer app - UserLayout provides nav + footer for all */}
                  <Route element={<UserLayout />}>
                    {/* Browse, cart & checkout without login; login required for online payment & account */}
                    <Route path="/explore" element={<UserSearch />} />
                    <Route path="/boulangerie/:ownerId" element={<BoulangerieItems />} />
                    <Route path="/restaurant/:restaurantId" element={<RestaurantMenuPage />} />
                    <Route
                      path="/restaurant/:restaurantId/gouts"
                      element={<RedirectRestaurantGouts />}
                    />
                    <Route
                      path="/restaurant/:restaurantId/offers"
                      element={<RedirectRestaurantOffers />}
                    />
                    <Route path="/u/:userRefCode/stats" element={<UserProfileStatsPage />} />
                    <Route path="/u/:userRefCode" element={<UserPublicProfilePage />} />
                    <Route path="/feed" element={<TastesFeedPage />} />
                    <Route path="/feed/combination/:postId" element={<CombinationTriedPage />} />
                    <Route path="/feed/item/:menuItemId" element={<ItemFeedPage />} />
                    <Route path="/t/:tasteId" element={<TastePostPage />} />
                    <Route path="/restaurant/:restaurantId/redeem" element={<RedeemPointsPage />} />
                    <Route path="/restaurant/:restaurantId/page" element={<RestaurantProfilePage />} />
                    <Route path="/restaurant/:restaurantId/dine-reserve" element={<DineInReservePage />} />
                    <Route path="/restaurant/:restaurantId/dining/table" element={<DiningTableSessionPage />} />
                    <Route path="/restaurant/:restaurantId/dining/reserve" element={<DiningPlatformReservePage />} />
                    <Route path="/new-selection/:itemId" element={<NewSelectionPage />} />
                    <Route path="/cart/:ownerId" element={<CartPage />} />
                    <Route path="/cart/:ownerId/counter-success" element={<CounterOrderSuccessPage />} />
                    <Route path="/checkout/pickup" element={<PickupWindowSelector />} />
                    <Route element={<UserProtectedRoute />}>
                      <Route path="/payment" element={<CheckoutPayment />} />
                      <Route path="/favorites" element={<FavoritesPage />} />
                      <Route path="/share-taste/:orderId" element={<ShareFromOrderPage />} />
                      <Route
                        path="/share-taste/:orderId/item/:itemKey"
                        element={<ShareOrderItemPage />}
                      />
                      <Route
                        path="/share-taste/:orderId/line/:lineIndex"
                        element={<ShareOrderItemPage />}
                      />
                      <Route path="/plan-for-today" element={<Navigate to="/favorites" replace />} />
                      <Route path="/planned/:id/checkout" element={<PlannedMealCheckoutPage />} />
                      <Route path="/orders" element={<UserOrdersPage />} />
                      <Route path="/messages/c/:conversationId" element={<MessagesPage role="user" />} />
                      <Route path="/messages/shop/:shopConversationId" element={<MessagesPage role="user" />} />
                      <Route path="/messages/:orderId" element={<MessagesPage role="user" />} />
                      <Route path="/messages" element={<MessagesPage role="user" />} />
                      <Route path="/orders/dining/:sessionId" element={<UserDiningSessionOrderPage />} />
                      <Route path="/orders/:orderId" element={<OrderPage embedded />} />
                      <Route path="/dine-in-reservations" element={<UserDineInReservationsPage />} />
                      <Route path="/success" element={<SuccessPage />} />
                      <Route path="/settings" element={<UserSettingsPage />} />
                      <Route path="/notifications" element={<NotificationInboxPage role="user" />} />
                      <Route path="/places/:placeId/edit" element={<EditSavedPlacePage />} />
                    </Route>
                    {/* Order page - accessible without auth; inside UserLayout for nav + footer */}
                    <Route path="/order/:orderId" element={<OrderPage embedded />} />
                    <Route path="/order/token/:token" element={<TokenDetails />} />
                  </Route>

                  {/* Owner app */}
                  <Route element={<OwnerProtectedRoute />}>
                    <Route path="/restaurant/set-password" element={<EmployeeSetPasswordPage />} />
                    <Route element={<DashboardLayout />}>
                      <Route path="/restaurant/dashboard" element={<Dashboard />} />
                      <Route path="/restaurant/feedback" element={<div>Feedback</div>} />
                      <Route path="/restaurant/inventory" element={<InventoryPage />} />
                      <Route path="/restaurant/orders" element={<OwnerOrders />} />
                      <Route path="/restaurant/order" element={<OwnerPhoneOrderPage />} />
                      <Route path="/restaurant/orders/map" element={<OwnerOrdersMapPage />} />
                      <Route path="/restaurant/orders/:orderId" element={<OrderPage embedded />} />
                      <Route path="/restaurant/messages/shop/:shopConversationId" element={<MessagesPage role="owner" />} />
                      <Route path="/restaurant/messages/:orderId" element={<MessagesPage role="owner" />} />
                      <Route path="/restaurant/messages" element={<MessagesPage role="owner" />} />
                      <Route path="/restaurant/students" element={<OwnerStudentsHubLayout />}>
                        <Route index element={<Navigate to="verify" replace />} />
                        <Route path="verify" element={<VerifyStudentPage />} />
                        <Route path="discount" element={<StudentDiscountPolicyPage />} />
                      </Route>
                      <Route path="/restaurant/loyalty" element={<OwnerLoyaltyPage />} />
                      <Route path="/restaurant/loyal-customers" element={<LoyalCustomersPage />} />
                      <Route path="/restaurant/payment-setup" element={<RestaurantPaymentSetup />} />
                      <Route path="/restaurant/menu" element={<MenuPage />} />
                      <Route path="/restaurant/timings" element={<OwnerTimingsHubLayout />}>
                        <Route index element={<OwnerTimingsHoursPage />} />
                        <Route path="modes" element={<OwnerTimingsModesPage />} />
                        <Route path="minimums" element={<Navigate to="/restaurant/timings/modes" replace />} />
                        <Route path="drive" element={<Navigate to="/restaurant/timings/modes" replace />} />
                        <Route path="delivery" element={<Navigate to="/restaurant/timings/modes" replace />} />
                      </Route>
                      <Route path="/restaurant/dine-in-tables" element={<OwnerDineInTablesPage />} />
                      <Route path="/restaurant/dining/console" element={<OwnerDiningConsolePage />} />
                      <Route path="/restaurant/settings" element={<OwnerSettingsPage />} />
                      <Route path="/restaurant/devices" element={<OwnerDevicesPage />} />
                      <Route path="/restaurant/order-rules" element={<OwnerOrderRulesPage />} />
                      <Route path="/restaurant/bulk-orders" element={<Navigate to="/restaurant/order-rules" replace />} />
                      <Route path="/restaurant/notifications" element={<NotificationInboxPage role="owner" />} />
                      <Route path="/restaurant/page" element={<OwnerRestaurantPage />} />
                      <Route path="/restaurant/stories" element={<OwnerStoriesPage />} />
                      <Route path="/restaurant/feed" element={<OwnerFeedPage />} />
                      <Route path="/restaurant/menu-builder" element={<OwnerMenuBuilder />} />
                      <Route path="/restaurant/offers" element={<OwnerOffersPage />} />
                      <Route path="/restaurant/employees" element={<EmployeesPage />} />
                    </Route>
                    <Route path="/restaurant/print" element={<PrintOrderPage />} />
                  </Route>

                  {/* Super Admin */}
                  <Route element={<SuperAdminProtectedRoute />}>
                    <Route path="/admin" element={<SuperAdmin />} />
                    <Route path="/admin/set-password" element={<SuperAdminSetPassword />} />
                  </Route>

                  {/* Catch-all: redirect unknown paths to explore (prevents blank on refresh) */}
                  <Route path="*" element={<Navigate to="/explore" replace />} />
                    </Routes>
                    </Suspense>
                  </div>
                </div>
              </Router>
              {/* Toaster for notifications */}
              <Toaster richColors position="top-right" />
          </CartProvider>
        </SuperAdminAuthProvider>
      </OwnerAuthProvider>
    </AuthProvider>
    </SiteSettingsProvider>
    </ErrorBoundary>
  );
};

export default App;
